import { Fragment, useMemo } from 'react'
import { useUIDSeed } from 'react-uid'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'

import { Column, Row } from '@hmn/rtl-web-core/components/layout'
import { capitalize } from '@hmn/rtl-web-core/helpers/capitalize'
import { useListingInsertAds } from '@hmn/rtl-web-core/hooks/article/useAds'
import { IsolatedMiscThirdParties } from '@hmn/rtl-web-core/hooks/category/useMiscThirdParties'
import useEntityTypeDispatch from '@hmn/rtl-web-core/hooks/entity/useEntityTypeDispatch'
import useCurrentPageNumber from '@hmn/rtl-web-core/hooks/useCurrentPageNumber'
import { adsGetServerSidePropsFactory } from '@hmn/rtl-web-core/next/data/adsGetServerSideProps'
import { navigationGetServerSideProps } from '@hmn/rtl-web-core/next/data/navigationGetServerSideProps'
import { sessionGetServerSideProps } from '@hmn/rtl-web-core/next/data/sessionGetServerSideProps'
import { getServerSideProps as themesSlugGetServerSideProps } from '@hmn/rtl-web-core/next/data-fetching/tag'
import combineGetServerSideProps from '@hmn/rtl-web-core/next/helpers/combineGetServerSideProps'

import { listingInsertAds as insertAdsConfig, staticAds } from '@hmn/rtl-zena-ui/components/Ad/config/general'
import AdSlot from '@hmn/rtl-zena-ui/components/Ad/ZenaSlot.component'
import { articleCardDecorationVariants, articleCardVariants } from '@hmn/rtl-zena-ui/components/Article/components/Card'
import { EntityCard } from '@hmn/rtl-zena-ui/components/EntityCard'
import { imageRatioVariants } from '@hmn/rtl-zena-ui/components/Image'
import { Pagination } from '@hmn/rtl-zena-ui/components/Pagination'
import { TagHead } from '@hmn/rtl-zena-ui/components/Tag/components'

import OutOfPageAdSlots from '../../components/OutOfPageAdSlots.component'
import Seo from '../../components/ZenaSeo.component'
import { BaseLayout } from '../../layouts'
import { authOptions } from '../api/auth/[...nextauth]'

const { billboardTop, outOfPage1, outOfPage3, anchor } = staticAds

const { DELTA: A, BETA: B, GAMMA: C } = articleCardVariants

const variantGrid = [
    [A, B],
    [C, C, C],
    [B, A],
    [C, C, C],
    [A, A],
    [C, C, C],
    [C, C, C]
].reduce((all, row) => [...all, ...row], [])

const getArticleVariant = index => (index < 18 ? variantGrid[index % variantGrid.length] : C)

const { ALPHA, BETA, GAMMA } = articleCardDecorationVariants

const decorationVariants = [
    [ALPHA, 0],
    [0, 0, 0],
    [0, BETA],
    [0, 0, 0],
    [GAMMA, 0],
    [0, 0, 0],
    [0, 0, 0]
].reduce((all, row) => [...all, ...row], [])

const getDecorationVariant = index =>
    index < 14 ? decorationVariants[index % decorationVariants.length] || undefined : undefined

const layoutGrid = [
    [6, 5],
    [4, 4, 4],
    [6, 6],
    [4, 4, 4],
    [6, 6],
    [4, 4, 4],
    [4, 4, 4]
].reduce((all, row) => [...all, ...row], [])

const getLayoutGridSize = index => (index < 14 ? layoutGrid[index % layoutGrid.length] : 4)

// Aspect ratio of Card images in listing
const { CUSTOM_SQUARE, CUSTOM_PORTRAIT } = imageRatioVariants

// // Width of Card images in listing
const imageVariation = [
    [CUSTOM_SQUARE, CUSTOM_SQUARE, CUSTOM_PORTRAIT],
    [CUSTOM_PORTRAIT, CUSTOM_PORTRAIT, CUSTOM_SQUARE],
    [CUSTOM_SQUARE, CUSTOM_PORTRAIT],
    [CUSTOM_PORTRAIT, CUSTOM_PORTRAIT, CUSTOM_SQUARE],
    [CUSTOM_SQUARE, CUSTOM_SQUARE, CUSTOM_SQUARE, CUSTOM_SQUARE]
].reduce((all, row) => [...all, ...row], [])

const getImageVariation = index => (index < 14 ? imageVariation[index % imageVariation.length] : CUSTOM_SQUARE)

const itemsPerGridRow = [2, 3, 2, 3, 2, 3, 3]
const getRowInfo = index => {
    let count = index + 1
    const lastRowIndex = itemsPerGridRow.length - 1
    let rowInd = 0
    while (count > 0) {
        count -= itemsPerGridRow[rowInd % lastRowIndex]
        rowInd += 1
    }
    return { index: rowInd - 1, lastInRow: count === 0 }
}

function Tag({ tag, articles, totalPages, totalItems, navigation, articlesPerPage }) {
    const router = useRouter()
    const uid = useUIDSeed()
    const { pageNumber, slug } = router.query

    useEntityTypeDispatch('tag')

    const currentPage = useCurrentPageNumber()

    const slugs = useMemo(() => ['tag', slug], [slug])
    const pageUrl = useMemo(
        () => (pageNumber ? `${process.env.NEXT_PUBLIC_APP_ROOT_ZENA}/tema/${slug}/${pageNumber}` : ''),
        [slug, pageNumber]
    )

    const tagTitle = useMemo(() => capitalize(tag.title), [tag.title])

    const seoItem = useMemo(
        () => ({
            breadcrumb: ['tag', slug],
            title: tagTitle,
            description: `Tag ${tagTitle}`,
            href: `/tema/${slug}${currentPage > 1 ? `?stranica=${currentPage}` : ''}`
        }),
        [slug, tagTitle, currentPage]
    )

    /** ***************ADS******************* */
    const insertAds = useListingInsertAds({
        insertAdsConfig,
        AdSlotComponent: AdSlot,
        ColumnComponent: Column
    })

    const gtmDataGeneralInfo = useMemo(
        () => ({
            event: 'pageView',
            userType: 'not logged in',
            pageType: 'tag(keyword) pages',
            contentID: tag.id,
            contentTitle: tag.title,
            contentAuthor: '',
            contentCreator: ''
        }),
        [tag.id, tag.title]
    )

    const gtmDataVirtualInfo = useMemo(
        () => ({
            event: 'VirtualPageView',
            virtualPageURL: `/tema/${tag.slug}${currentPage > 1 ? `?stranica=${currentPage}` : ''}`,
            virtualPageTitle: tagTitle
        }),
        [currentPage, tag.slug, tagTitle]
    )

    return (
        <BaseLayout navigationData={navigation}>
            <IsolatedMiscThirdParties
                slugs={slugs}
                pageUrl={pageUrl}
                gemiusId={process.env.NEXT_PUBLIC_GEMIUS_ID_ZENA}
                appRoot={process.env.NEXT_PUBLIC_APP_ROOT_ZENA}
                gtmDataGeneralInfo={gtmDataGeneralInfo}
                gtmDataVirtualInfo={gtmDataVirtualInfo}
            />
            <Seo mainEntity item={seoItem} page={currentPage} listItems={articles} />
            <Row variant="tag.main">
                <Column variant="tag.head">
                    <TagHead label="tema" title={tagTitle} />
                </Column>
                <Row variant="tag.ads.billboard">
                    <AdSlot {...billboardTop} />
                </Row>
                <Column variant="tag.listing">
                    {!!articles?.length &&
                        articles?.map((item, index) => {
                            // @TODO: implement ConditionalInsert here
                            const { index: rowIndex, lastInRow: isLastInRow } = getRowInfo(index)
                            const offsetRowIndex = rowIndex + 1 - insertAds[0].offset
                            const rowMatchesAdPattern = offsetRowIndex % insertAds[0].every === 0 && offsetRowIndex >= 0
                            const insertAdIndex = Math.floor((rowIndex - insertAds[0].offset) / insertAds[0].every)
                            return (
                                <Fragment key={uid(item)}>
                                    <Column
                                        variant={
                                            index === 0
                                                ? 'tag.listing.itemLarge'
                                                : `grid.12.col.${getLayoutGridSize(index)}x1`
                                        }
                                        className={index === 1 || index === 5 ? `u-alignSelf_center` : null}>
                                        <EntityCard
                                            item={item}
                                            imageVariation={getImageVariation(index)}
                                            cardVariant={getArticleVariant(index)}
                                            cardDecorationVariant={getDecorationVariant(index)}
                                            imageLazyLoad={index > 2}
                                            imagePreload={index === 0}
                                        />
                                    </Column>
                                    {isLastInRow &&
                                        rowMatchesAdPattern &&
                                        insertAdIndex < insertAds[0].elements.length &&
                                        insertAds[0].elements[insertAdIndex]}
                                </Fragment>
                            )
                        })}
                </Column>
                {totalItems > articlesPerPage ? (
                    <Row variant="tag.loadMore">
                        <Pagination pageTotal={totalPages} selected={currentPage} pageName={seoItem.title} />
                    </Row>
                ) : null}
            </Row>
            <OutOfPageAdSlots config={staticAds} />
        </BaseLayout>
    )
}

Tag.propTypes = {
    tag: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        slug: PropTypes.string
    }).isRequired,
    articles: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            sitemap: PropTypes.shape({
                href: PropTypes.string
            }),
            type: PropTypes.string
        })
    ).isRequired,
    totalItems: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    navigation: PropTypes.shape({}).isRequired,
    articlesPerPage: PropTypes.number.isRequired
}

export const getServerSideProps = combineGetServerSideProps([
    themesSlugGetServerSideProps('zena'),
    sessionGetServerSideProps(authOptions),
    adsGetServerSidePropsFactory({
        pageType: 'tags',
        zones: { billboardTop, outOfPage1, outOfPage3, anchor },
        insertAdsConfig
    }),
    navigationGetServerSideProps
])

export default Tag
